/**
 * Tracks the exact durable Conversation selected for the input bar. Runtime
 * segment identity is supplemental and can never address a chat by itself.
 * A selected Conversation persists across turns until New Chat or another
 * explicit selection.
 *
 * Also owns the one-shot "new conversation" request made by /new. That flag
 * has to live next to the explicit target because both affect the same
 * prompt.send routing decision.
 */

import { create } from "zustand";
import {
  isSubagentTarget,
  type InputBarTargetKind,
} from "@/lib/input-bar-target-kind";
import { mirroredStore } from "@/lib/mirrored-store";
import { useAgentStore } from "@/stores/agent-store";

export interface InputBarTarget {
  /** Canonical durable chat address. */
  conversationId: string;
  /** Chat (also when absent, for older mirrored targets) or native sub-agent. */
  kind?: InputBarTargetKind;
  /** Task owning the conversation. A sub-agent's worker state is read through
   *  it; a task-linked chat keeps it only for its icon. */
  taskId?: string;
  /** Exact nullable segment captured with runtimeGeneration. */
  runtimeSessionId?: string | null;
  runtimeGeneration?: number;
  selectionGeneration?: number;
  /** Stable routing key returned by the exact selection endpoint. */
  agentKey?: string;
  projectAgentId: string;
  agentName: string;
  /** Optional ticket label (e.g. "WEL-4"). Resolved at click time when known. */
  ticket: string | null;
  /** Human-readable chat title shown in the input bar. */
  conversationLabel?: string;
  /** Project copied from the selected chat for visible routing. */
  projectId?: string;
  projectName?: string;
  /** @deprecated Project-id alias retained for older input dispatch contracts. */
  spaceId?: string;
  /** Persisted usage for this exact chat before live runtime tokens arrive. */
  persistedContextPercent?: number | null;
  persistedContextUsedTokens?: number | null;
  persistedContextWindowMax?: number | null;
  /** Runtime of the chat's latest segment; a reply keeps this runtime. */
  runtimeAdapterType?: string | null;
  runtimeModel?: string | null;
  runtimeThinkingEffort?: string | null;
  /** Managed source (e.g. "denker_starter"); empty means the user's own CLI. */
  runtimeSource?: string | null;
  /** Backend-decided: a reply no longer resumes this runtime (e.g. ended demo). */
  runtimeOverrideExpired?: boolean;
  /** Internal correlation for successive receipts from one routed selection. */
  acceptedRuntimeIntentId?: string;
  /** Picked row's pending_next_prompt; used only until the store has the chat's messages. */
  pendingNextPrompt?: string;
}

/** Runtime identity reported by an accepted prompt receipt. */
export interface ReportedRuntime {
  adapterType?: string | null;
  model?: string | null;
  thinkingEffort?: string | null;
  runtimeSource?: string | null;
}

export interface AcceptedRuntime extends ReportedRuntime {
  runtimeSessionId: string;
}

/**
 * A receipt reports the runtime as a whole: once it names the adapter, a
 * missing key means empty, and the runtime just dispatched is not expired.
 * Without an adapter (older backends) nothing is reported and stored values
 * stay.
 */
export function reportedRuntimeFields(
  runtime: ReportedRuntime,
): Partial<InputBarTarget> {
  if (runtime.adapterType === undefined) return {};
  return {
    runtimeAdapterType: runtime.adapterType,
    runtimeModel: runtime.model ?? null,
    runtimeThinkingEffort: runtime.thinkingEffort ?? null,
    runtimeSource: runtime.runtimeSource ?? null,
    runtimeOverrideExpired: false,
  };
}

function acceptedRuntimeFieldChanges(
  target: InputBarTarget,
  runtime: AcceptedRuntime,
): Partial<InputBarTarget> {
  return Object.fromEntries(
    Object.entries(reportedRuntimeFields(runtime)).filter(
      ([key, value]) => target[key as keyof InputBarTarget] !== value,
    ),
  );
}

export interface InputBarRoutingIntent {
  target: InputBarTarget | null;
  startNewConversation: boolean;
  newConversationSource?: NewConversationSource | null;
  intentId: string | null;
  newConversationAgentId?: string | null;
  newConversationFrameId?: string | null;
}

export type NewConversationSource = "input" | "voice";

const NEW_CHAT_CLAIM_LEASE_MS = 35_000;
const ROUTING_REQUEST_TTL_MS = 10 * 60_000;
const MAX_ROUTING_REQUESTS = 50;

export interface PromptRoutingRequest {
  intent: InputBarRoutingIntent;
  claimOwnerId: string;
  expiresAt: number;
}

interface InputBarTargetState {
  target: InputBarTarget | null;
  startNewConversation: boolean;
  newConversationSource: NewConversationSource | null;
  intentId: string | null;
  newConversationAgentId: string | null;
  newConversationFrameId: string | null;
  claimedIntentId: string | null;
  claimOwnerId: string | null;
  claimExpiresAt: number | null;
  promptRoutingRequests: Record<string, PromptRoutingRequest>;
  setTarget: (target: InputBarTarget | null) => void;
  clearTarget: () => void;
  requestNewConversation: (projectAgentId?: string) => void;
  requestVoiceNewConversation: (conversationFrameId?: string | null) => void;
  snapshotRoutingIntent: () => InputBarRoutingIntent;
  /** Puts back an earlier route as it was, keeping its intent id so an
   *  in-flight send can still adopt the chat it creates. */
  restoreRoutingIntent: (intent: InputBarRoutingIntent) => void;
  claimNewConversationIntent: (
    intent: InputBarRoutingIntent,
    ownerId: string,
    now?: number,
    allowTakeover?: boolean,
  ) => boolean;
  releaseNewConversationClaim: (
    intent: InputBarRoutingIntent,
    ownerId: string,
  ) => boolean;
  registerPromptRoutingRequest: (
    requestId: string,
    intent: InputBarRoutingIntent,
    claimOwnerId: string,
  ) => void;
  settlePromptRoutingRequest: (requestId: string, accepted: boolean) => boolean;
  consumeRoutingIntent: (intent: InputBarRoutingIntent) => boolean;
  adoptAcceptedRuntime: (
    submittedIntent: InputBarRoutingIntent,
    runtime: AcceptedRuntime,
  ) => boolean;
  /** Drops the picked row's prediction once its target has been submitted
   *  to. The single clear mechanism (see submitInputBarTarget) — no-op when
   *  there is no target or no prediction to clear. */
  clearPendingNextPrompt: () => void;
}

export function createInputBarRoutingIntentId(): string {
  return crypto.randomUUID();
}

export const useInputBarTarget = create<InputBarTargetState>()(
  mirroredStore("inputBarTarget", (set, get) => ({
    target: null,
    startNewConversation: false,
    newConversationSource: null,
    intentId: null,
    newConversationAgentId: null,
    newConversationFrameId: null,
    claimedIntentId: null,
    claimOwnerId: null,
    claimExpiresAt: null,
    promptRoutingRequests: {},
    setTarget: (target) =>
      set({
        target,
        startNewConversation: false,
        newConversationSource: null,
        intentId: target ? createInputBarRoutingIntentId() : null,
        newConversationAgentId: null,
        newConversationFrameId: null,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      }),
    clearTarget: () =>
      set({
        target: null,
        startNewConversation: false,
        newConversationSource: null,
        intentId: null,
        newConversationAgentId: null,
        newConversationFrameId: null,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      }),
    requestNewConversation: (projectAgentId) => {
      set({
        target: null,
        startNewConversation: true,
        newConversationSource: "input",
        intentId: createInputBarRoutingIntentId(),
        newConversationAgentId:
          projectAgentId ?? useAgentStore.getState().selectedAgentId,
        newConversationFrameId: null,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      });
    },
    requestVoiceNewConversation: (conversationFrameId = null) => {
      set({
        target: null,
        startNewConversation: true,
        newConversationSource: "voice",
        intentId: createInputBarRoutingIntentId(),
        newConversationAgentId: useAgentStore.getState().selectedAgentId,
        newConversationFrameId: conversationFrameId,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      });
    },
    snapshotRoutingIntent: () => {
      const state = get();
      return {
        target: state.target,
        startNewConversation: state.startNewConversation,
        newConversationSource: state.newConversationSource,
        intentId: state.intentId,
        newConversationAgentId: state.newConversationAgentId,
        newConversationFrameId: state.newConversationFrameId,
      };
    },
    restoreRoutingIntent: (intent) =>
      set({
        target: intent.target,
        startNewConversation: intent.startNewConversation,
        newConversationSource: intent.startNewConversation
          ? (intent.newConversationSource ?? "input")
          : null,
        intentId: intent.intentId,
        newConversationAgentId: intent.newConversationAgentId ?? null,
        newConversationFrameId: intent.newConversationFrameId ?? null,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      }),
    claimNewConversationIntent: (
      intent,
      ownerId,
      now = Date.now(),
      allowTakeover = false,
    ) => {
      const state = get();
      if (!intent.startNewConversation || !intent.intentId) {
        return false;
      }
      const leaseIsLive =
        state.claimedIntentId === intent.intentId &&
        state.claimOwnerId &&
        (state.claimExpiresAt ?? 0) > now;
      if (leaseIsLive && state.claimOwnerId !== ownerId && !allowTakeover) {
        return false;
      }
      set({
        claimedIntentId: intent.intentId,
        claimOwnerId: ownerId,
        claimExpiresAt: now + NEW_CHAT_CLAIM_LEASE_MS,
      });
      return true;
    },
    releaseNewConversationClaim: (intent, ownerId) => {
      const state = get();
      if (
        state.claimedIntentId !== intent.intentId ||
        state.claimOwnerId !== ownerId
      ) {
        return false;
      }
      set({
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      });
      return true;
    },
    registerPromptRoutingRequest: (requestId, intent, claimOwnerId) => {
      const now = Date.now();
      const entries = Object.entries(get().promptRoutingRequests)
        .filter(([, request]) => request.expiresAt > now)
        .sort((first, second) => second[1].expiresAt - first[1].expiresAt)
        .slice(0, MAX_ROUTING_REQUESTS - 1);
      set({
        promptRoutingRequests: {
          ...Object.fromEntries(entries),
          [requestId]: {
            intent,
            claimOwnerId,
            expiresAt: now + ROUTING_REQUEST_TTL_MS,
          },
        },
      });
    },
    settlePromptRoutingRequest: (requestId, accepted) => {
      const request = get().promptRoutingRequests[requestId];
      if (!request) {
        const state = get();
        const matchesIntent = state.intentId === requestId;
        const matchesClaim = state.claimedIntentId === requestId;
        if (!matchesIntent && !matchesClaim) return false;
        if (accepted && matchesIntent) {
          get().consumeRoutingIntent(get().snapshotRoutingIntent());
        } else if (matchesClaim) {
          set({
            claimedIntentId: null,
            claimOwnerId: null,
            claimExpiresAt: null,
          });
        }
        return true;
      }
      if (request.expiresAt <= Date.now()) {
        const { [requestId]: _expired, ...remaining } =
          get().promptRoutingRequests;
        set({ promptRoutingRequests: remaining });
        return false;
      }
      if (accepted) get().consumeRoutingIntent(request.intent);
      get().releaseNewConversationClaim(request.intent, request.claimOwnerId);
      const { [requestId]: _settled, ...remaining } =
        get().promptRoutingRequests;
      set({ promptRoutingRequests: remaining });
      return true;
    },
    consumeRoutingIntent: (intent) => {
      if (!intent.intentId || get().intentId !== intent.intentId) return false;
      if (intent.target?.conversationId) {
        const currentTarget = get().target;
        const consumedTarget =
          currentTarget?.conversationId === intent.target.conversationId &&
          currentTarget.projectAgentId === intent.target.projectAgentId
            ? currentTarget
            : intent.target;
        set({
          target: consumedTarget,
          startNewConversation: false,
          newConversationSource: null,
          intentId: createInputBarRoutingIntentId(),
          newConversationAgentId: null,
          newConversationFrameId: null,
          claimedIntentId: null,
          claimOwnerId: null,
          claimExpiresAt: null,
        });
        return true;
      }
      set({
        target: null,
        startNewConversation: false,
        newConversationSource: null,
        intentId: null,
        newConversationAgentId: null,
        newConversationFrameId: null,
        claimedIntentId: null,
        claimOwnerId: null,
        claimExpiresAt: null,
      });
      return true;
    },
    adoptAcceptedRuntime: (submittedIntent, runtime) => {
      // A sub-agent target has no runtime receipts or generations.
      if (isSubagentTarget(get().target)) return false;
      const { runtimeSessionId } = runtime;
      const state = get();
      const currentTarget = state.target;
      const submittedTarget = submittedIntent.target;
      if (
        !currentTarget ||
        !submittedTarget ||
        !submittedIntent.intentId ||
        currentTarget.conversationId !== submittedTarget.conversationId ||
        currentTarget.projectAgentId !== submittedTarget.projectAgentId ||
        (state.intentId !== submittedIntent.intentId &&
          currentTarget.acceptedRuntimeIntentId !== submittedIntent.intentId)
      ) {
        return false;
      }
      const runtimeChanges = acceptedRuntimeFieldChanges(
        currentTarget,
        runtime,
      );
      if (currentTarget.runtimeSessionId === runtimeSessionId) {
        if (
          currentTarget.acceptedRuntimeIntentId === submittedIntent.intentId &&
          Object.keys(runtimeChanges).length === 0
        ) {
          return true;
        }
        set({
          target: {
            ...currentTarget,
            ...runtimeChanges,
            acceptedRuntimeIntentId: submittedIntent.intentId,
          },
        });
        return true;
      }
      set({
        target: {
          ...currentTarget,
          ...runtimeChanges,
          runtimeSessionId,
          acceptedRuntimeIntentId: submittedIntent.intentId,
          persistedContextPercent: undefined,
          persistedContextUsedTokens: undefined,
          persistedContextWindowMax: undefined,
        },
      });
      return true;
    },
    clearPendingNextPrompt: () => {
      const target = get().target;
      if (!target?.pendingNextPrompt) return;
      set({ target: { ...target, pendingNextPrompt: undefined } });
    },
  })),
);
